"use client";

import {
  createContext,
  FC,
  ReactNode,
  Ref,
  useRef,
  useState,
  useEffect,
  useMemo,
} from "react";
import SlickSlider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { executeOnAllSliders } from "./Slider.helpers";

export type SliderEventHandlers = {
  onControlsReady?: (controls: SliderExternalControls) => void;
  onSlideChange?: (currentIndex: number, previousIndex: number) => void;
  onBeforeSlideChange?: (currentIndex: number, nextIndex: number) => void;
  onAfterSlideChange?: (currentIndex: number) => void;
  onNext?: (currentIndex: number) => void;
  onPrevious?: (currentIndex: number) => void;
  onGoToSlide?: (targetIndex: number, currentIndex: number) => void;
};

export type SliderExternalControls = {
  goToNext: () => void;
  goToPrevious: () => void;
  goToSlide: (index: number) => void;
  getCurrentIndex: () => number;
  getItemsCount: () => number;
};

type SliderContextType = {
  currentIndex: number;
  itemsCount: number;
  sliderRefs: Set<Ref<SlickSlider>>;
  config?: SliderConfig;
  eventHandlers?: SliderEventHandlers;
  setItemsCount: (count: number) => void;
  setCurrentIndex: (index: number) => void;
  registerSliderRef: (ref: Ref<SlickSlider>) => void;
  unregisterSliderRef: (ref: Ref<SlickSlider>) => void;
  triggerSlideChange: (nextIndex: number, currentIndex: number) => void;
  triggerBeforeSlideChange: (
    nextIndex: number,
    currentIndex: number,
    initiatorRef?: Ref<SlickSlider>,
  ) => void;
  triggerAfterSlideChange: (currentIndex: number) => void;
};

export const SliderContext = createContext<SliderContextType>({
  currentIndex: 0,
  itemsCount: 0,
  sliderRefs: new Set(),
  setItemsCount: () => {},
  setCurrentIndex: () => {},
  registerSliderRef: () => {},
  unregisterSliderRef: () => {},
  triggerSlideChange: () => {},
  triggerBeforeSlideChange: () => {},
  triggerAfterSlideChange: () => {},
});

export type SliderConfig = {
  initialSlide?: number;
  autoplay?: boolean;
  autoplaySpeed?: number;
  infinite?: boolean;
  fade?: boolean;
  vertical?: boolean;
  verticalSwiping?: boolean
  adaptiveHeight?: boolean;
  swipe?: boolean;
  draggable?: boolean;
  touchMove?: boolean;
  pauseOnHover?: boolean;
  pauseOnFocus?: boolean;
  responsive?: { breakpoint: number; settings: SliderConfig }[];
  className?: string;
};

type SliderProps = {
  children: ReactNode;
  eventHandlers?: SliderEventHandlers;
} & SliderConfig;

export const Slider: FC<SliderProps> = ({
  initialSlide = 0,
  children,
  eventHandlers,
  ...config
}) => {
  const [currentIndex, setCurrentIndex] = useState(initialSlide);
  const [itemsCount, setItemsCount] = useState(0);
  const sliderRefs = useRef(new Set<Ref<SlickSlider>>());
  const controlsReadyCalledRef = useRef(false);
  const isSyncingRef = useRef(false);

  const externalControls: SliderExternalControls = useMemo(
    () => ({
      goToNext: () => {
        executeOnAllSliders(sliderRefs.current, (slider) => slider.slickNext());
        eventHandlers?.onNext?.(currentIndex);
      },
      goToPrevious: () => {
        executeOnAllSliders(sliderRefs.current, (slider) => slider.slickPrev());
        eventHandlers?.onPrevious?.(currentIndex);
      },
      goToSlide: (index: number) => {
        executeOnAllSliders(sliderRefs.current, (slider) =>
          slider.slickGoTo(index),
        );
        eventHandlers?.onGoToSlide?.(index, currentIndex);
      },
      getCurrentIndex: () => currentIndex,
      getItemsCount: () => itemsCount,
    }),
    [eventHandlers, currentIndex, itemsCount],
  );

  useEffect(() => {
    if (
      eventHandlers?.onControlsReady &&
      sliderRefs.current.size > 0 &&
      !controlsReadyCalledRef.current
    ) {
      const timeoutId = setTimeout(() => {
        eventHandlers.onControlsReady!(externalControls);
        controlsReadyCalledRef.current = true;
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [eventHandlers?.onControlsReady, externalControls]);

  return (
    <SliderContext.Provider
      value={{
        currentIndex,
        itemsCount,
        sliderRefs: sliderRefs.current,
        config,
        eventHandlers,
        setItemsCount,
        setCurrentIndex,
        registerSliderRef: (ref: Ref<SlickSlider>) => {
          sliderRefs.current.add(ref);
        },
        unregisterSliderRef: (ref: Ref<SlickSlider>) => {
          sliderRefs.current.delete(ref);
        },
        triggerSlideChange: (nextIndex: number, currentIndex: number) => {
          eventHandlers?.onSlideChange?.(nextIndex, currentIndex);
        },
        triggerBeforeSlideChange: (
          nextIndex: number,
          currentIndex: number,
          initiatorRef?: Ref<SlickSlider>,
        ) => {
          if (isSyncingRef.current) {
            return;
          }

          isSyncingRef.current = true;

          executeOnAllSliders(sliderRefs.current, (slider, ref) => {
            if (ref !== initiatorRef) {
              slider.slickGoTo(nextIndex);
            }
          });

          eventHandlers?.onBeforeSlideChange?.(currentIndex, nextIndex);
        },
        triggerAfterSlideChange: (currentIndex: number) => {
          eventHandlers?.onAfterSlideChange?.(currentIndex);
          isSyncingRef.current = false;
        },
      }}
    >
      {children}
    </SliderContext.Provider>
  );
};
